/**
 * @fileoverview NotaRepository - Repositorio especializado para notas
 */

import { DataRepository } from './base/DataRepository';
import type { 
  Nota, 
  NotaExpanded,
  CreateNotaData, 
  UpdateNotaData 
} from '../types/collections/Nota';
import type { ListResult, QueryOptions } from '../types/common/PocketBaseTypes';
import { RepositoryError } from '../types/common/PocketBaseTypes';
import { createModuleLogger } from '../utils/logger';

/**
 * Repositorio especializado para la gestión de notas
 * Extiende DataRepository con métodos específicos del dominio
 */
export class NotaRepository extends DataRepository<Nota> {
  private notaLogger = createModuleLogger('NotaRepository');
  
  constructor() {
    super('notas');
  }

  /**
   * Crea una nueva nota con asociación de usuario
   * @param createData Datos para crear la nota
   * @returns Nota creada
   */
  async createNote(createData: CreateNotaData): Promise<Nota> {
    try {
      this.notaLogger.info('Creating new note', { usuario: createData.usuario });
      
      // Validar datos requeridos
      if (!createData.nota || createData.nota.trim().length === 0) {
        throw new RepositoryError('Note content is required', 'VALIDATION_ERROR');
      }
      
      if (!createData.usuario || createData.usuario.trim().length === 0) {
        throw new RepositoryError('User ID is required', 'VALIDATION_ERROR');
      }

      // Validar longitud mínima de la nota
      if (createData.nota.trim().length < 10) {
        throw new RepositoryError('Note must be at least 10 characters long', 'VALIDATION_ERROR');
      }

      // Crear la nota (sin orden_produccion ya que no existe ese campo en la tabla notas)
      const { orden_produccion, ...notaData } = createData;
      const nota = await this.create(notaData);
      
      // Asociar la nota a la orden de producción
      if (orden_produccion) {
        await this.associateNoteToOrder(nota.id, orden_produccion);
      }
      
      this.notaLogger.info('Note created and associated successfully', { notaId: nota.id, orderId: orden_produccion });
      
      return nota;
    } catch (error) {
      this.notaLogger.error('Failed to create note', { error, createData });
      throw error;
    }
  }

  /**
   * Asocia una nota existente a una orden de producción
   * @param notaId ID de la nota
   * @param orderId ID de la orden de producción
   */
  private async associateNoteToOrder(notaId: string, orderId: string): Promise<void> {
    try {
      this.notaLogger.info('Associating note to order', { notaId, orderId });
      
      // Obtener la orden actual para ver las notas existentes
      const orden = await this.pb.collection('orden_produccion').getOne(orderId);
      const notasExistentes = orden.notas || [];
      
      // Agregar la nueva nota a la lista
      const nuevasNotas = [...notasExistentes, notaId];
      
      // Actualizar la orden con la nueva lista de notas
      await this.pb.collection('orden_produccion').update(orderId, {
        notas: nuevasNotas
      });
      
      this.notaLogger.info('Note associated to order successfully', { notaId, orderId });
    } catch (error) {
      this.notaLogger.error('Failed to associate note to order', { error, notaId, orderId });
      throw error;
    }
  }

  /**
   * Obtiene todas las notas asociadas a una orden de producción
   * @param orderId ID de la orden de producción
   * @returns Array de notas ordenadas por fecha de creación (más recientes primero)
   */
  async getNotesByOrderId(orderId: string): Promise<Nota[]> {
    try {
      this.notaLogger.info('Retrieving notes for order', { orderId });
      
      if (!orderId || orderId.trim().length === 0) {
        throw new RepositoryError('Order ID is required', 'VALIDATION_ERROR');
      }

      // Obtener la orden para ver qué notas están asociadas
      const orden = await this.pb.collection('orden_produccion').getOne(orderId);
      const notaIds = orden.notas || [];
      
      if (notaIds.length === 0) {
        this.notaLogger.info('No notes found for order', { orderId });
        return [];
      }

      // Obtener las notas por sus IDs
      const notas = await Promise.all(
        notaIds.map(async (notaId: string) => {
          try {
            return await this.getById(notaId);
          } catch (error) {
            this.notaLogger.warn('Note not found, skipping', { notaId, error });
            return null;
          }
        })
      );

      // Filtrar notas válidas y ordenar por fecha de creación
      const notasValidas = notas
        .filter(nota => nota !== null)
        .sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime());

      this.notaLogger.info('Notes retrieved successfully', { 
        orderId, 
        count: notasValidas.length 
      });
      
      return notasValidas;
    } catch (error) {
      this.notaLogger.error('Failed to retrieve notes for order', { error, orderId });
      throw error;
    }
  }

  /**
   * Obtiene todas las notas asociadas a una orden de producción con relaciones expandidas
   * @param orderId ID de la orden de producción
   * @returns Array de notas con usuario expandido
   */
  async getNotesByOrderIdExpanded(orderId: string): Promise<NotaExpanded[]> {
    try {
      this.notaLogger.info('Retrieving expanded notes for order', { orderId });
      
      if (!orderId || orderId.trim().length === 0) {
        throw new RepositoryError('Order ID is required', 'VALIDATION_ERROR');
      }

      // Obtener la orden para ver qué notas están asociadas
      const orden = await this.pb.collection('orden_produccion').getOne(orderId);
      const notaIds = orden.notas || [];
      
      if (notaIds.length === 0) {
        this.notaLogger.info('No notes found for order', { orderId });
        return [];
      }

      // Obtener las notas con relaciones expandidas por sus IDs
      const notas = await Promise.all(
        notaIds.map(async (notaId: string) => {
          try {
            return await this.pb.collection('notas').getOne(notaId, {
              expand: 'usuario'
            });
          } catch (error) {
            this.notaLogger.warn('Note not found, skipping', { notaId, error });
            return null;
          }
        })
      );

      // Filtrar notas válidas y ordenar por fecha de creación
      const notasValidas = notas
        .filter(nota => nota !== null)
        .sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime());

      this.notaLogger.info('Expanded notes retrieved successfully', { 
        orderId, 
        count: notasValidas.length 
      });
      
      return notasValidas as NotaExpanded[];
    } catch (error) {
      this.notaLogger.error('Failed to retrieve expanded notes for order', { error, orderId });
      throw error;
    }
  }

  /**
   * Obtiene una nota por su ID
   * @param notaId ID de la nota
   * @returns Nota encontrada
   */
  async getNoteById(notaId: string): Promise<Nota> {
    try {
      this.notaLogger.info('Retrieving note by ID', { notaId });
      
      if (!notaId || notaId.trim().length === 0) {
        throw new RepositoryError('Note ID is required', 'VALIDATION_ERROR');
      }

      const nota = await this.getById(notaId);
      this.notaLogger.info('Note retrieved successfully', { notaId });
      
      return nota;
    } catch (error) {
      this.notaLogger.error('Failed to retrieve note by ID', { error, notaId });
      throw error;
    }
  }

  /**
   * Actualiza una nota existente
   * @param notaId ID de la nota a actualizar
   * @param updateData Datos para actualizar
   * @returns Nota actualizada
   */
  async updateNote(notaId: string, updateData: UpdateNotaData): Promise<Nota> {
    try {
      this.notaLogger.info('Updating note', { notaId });
      
      if (!notaId || notaId.trim().length === 0) {
        throw new RepositoryError('Note ID is required', 'VALIDATION_ERROR');
      }

      // Validar contenido de la nota si se proporciona
      if (updateData.nota !== undefined) {
        if (!updateData.nota || updateData.nota.trim().length === 0) {
          throw new RepositoryError('Note content cannot be empty', 'VALIDATION_ERROR');
        }
        
        if (updateData.nota.trim().length < 10) {
          throw new RepositoryError('Note must be at least 10 characters long', 'VALIDATION_ERROR');
        }
      }

      const nota = await this.update(notaId, updateData);
      this.notaLogger.info('Note updated successfully', { notaId });
      
      return nota;
    } catch (error) {
      this.notaLogger.error('Failed to update note', { error, notaId, updateData });
      throw error;
    }
  }

  /**
   * Elimina una nota
   * @param notaId ID de la nota a eliminar
   * @returns true si se eliminó correctamente
   */
  async deleteNote(notaId: string): Promise<boolean> {
    try {
      this.notaLogger.info('Deleting note', { notaId });
      
      if (!notaId || notaId.trim().length === 0) {
        throw new RepositoryError('Note ID is required', 'VALIDATION_ERROR');
      }

      await this.delete(notaId);
      this.notaLogger.info('Note deleted successfully', { notaId });
      
      return true;
    } catch (error) {
      this.notaLogger.error('Failed to delete note', { error, notaId });
      throw error;
    }
  }

  /**
   * Obtiene el conteo de notas para una orden específica
   * @param orderId ID de la orden de producción
   * @returns Número de notas asociadas a la orden
   */
  async getNotesCountByOrderId(orderId: string): Promise<number> {
    try {
      this.notaLogger.info('Getting notes count for order', { orderId });
      
      if (!orderId || orderId.trim().length === 0) {
        throw new RepositoryError('Order ID is required', 'VALIDATION_ERROR');
      }

      // Obtener la orden para ver qué notas están asociadas
      const orden = await this.pb.collection('orden_produccion').getOne(orderId);
      const notaIds = orden.notas || [];
      
      this.notaLogger.info('Notes count retrieved successfully', { 
        orderId, 
        count: notaIds.length 
      });
      
      return notaIds.length;
    } catch (error) {
      this.notaLogger.error('Failed to get notes count for order', { error, orderId });
      throw error;
    }
  }

  /**
   * Verifica si una orden tiene notas
   * @param orderId ID de la orden de producción
   * @returns true si la orden tiene notas, false en caso contrario
   */
  async hasNotes(orderId: string): Promise<boolean> {
    try {
      const count = await this.getNotesCountByOrderId(orderId);
      return count > 0;
    } catch (error) {
      this.notaLogger.error('Failed to check if order has notes', { error, orderId });
      throw error;
    }
  }
}
